import { useEffect, useMemo } from 'react';
import { sortBy } from 'lodash';
import { Form, FormInstance, Select } from 'antd';
import { AtlasGqlAsset } from 'types/atlas-graphql';
import { getAssetColumnCopy } from 'components/data/assets/assetColumnRenderers';

const mapAndSort = (assets: AtlasGqlAsset[]) =>
  sortBy(
    assets.map(asset => ({
      id: asset.id,
      name: getAssetColumnCopy({ asset, separatorText: ' | ' }),
    })),
    'name'
  );

type DamageEditAssetProps = {
  form: FormInstance;
  assetId: string;
  onChange: (assetId: string) => void;
  potentialAssets: {
    likely: AtlasGqlAsset[];
    nearby: AtlasGqlAsset[];
  };
  externalLoading: boolean;
};

export default function DamageEditAsset({
  form,
  assetId,
  onChange,
  potentialAssets,
  externalLoading,
}: DamageEditAssetProps) {
  const { likelyAssetOptions, nearbyAssetOptions } = useMemo(() => {
    return {
      likelyAssetOptions: mapAndSort(potentialAssets.likely),
      nearbyAssetOptions: mapAndSort(potentialAssets.nearby),
    };
  }, [potentialAssets]);

  useEffect(() => {
    const _assetId =
      assetId ?? mapAndSort([...potentialAssets.likely, ...potentialAssets.nearby])[0]?.id;
    if (_assetId) {
      form.setFieldsValue({ assetId: _assetId });
      onChange(_assetId);
    }
  }, [form, assetId, onChange, potentialAssets]);

  return (
    <Form.Item
      label="Damage Asset"
      name="assetId"
      rules={[{ required: true, message: 'Please select an Asset' }]}
    >
      <Select
        className="testid-damage-asset-edit"
        style={{ width: '100%' }}
        allowClear={false}
        onChange={onChange}
        getPopupContainer={triggerNode => triggerNode.parentElement}
        loading={externalLoading}
      >
        <Select.OptGroup key={'likely'} label={'Likely Assets'}>
          {likelyAssetOptions.map(({ id, name }) => (
            <Select.Option key={id} value={id}>
              {name}
            </Select.Option>
          ))}
        </Select.OptGroup>
        <Select.OptGroup key={'nearby'} label={'Nearby Assets'}>
          {nearbyAssetOptions.map(({ id, name }) => (
            <Select.Option key={id} value={id}>
              {name}
            </Select.Option>
          ))}
        </Select.OptGroup>
      </Select>
    </Form.Item>
  );
}
